import { useEffect, useMemo, useState, useRef } from "react";
import { Routes, Route, Link } from "react-router-dom";
import StoryPage from "./StoryPage";
import StudioPage from "./StudioPage";
import PressPage from "./PressPage";
import PlaybookPage from "./PlaybookPage";
import { useSocialProof, SocialProofToast } from "./SocialProof";
import "@/App.css";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import {
  Download,
  Sparkles,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  BookOpen,
  Heart,
  ShoppingCart,
  Gift,
  Loader2,
  Target,
  GraduationCap,
  Copy,
  ExternalLink,
  Package,
  Truck,
  Music,
  X,
  Play,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  Mail,
  MessageCircle,
  Instagram,
  Film,
  Volume2,
  VolumeX,
  CreditCard,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  "Civil Rights": "#9A3B2C",
  Athletes: "#2F5D50",
  Musicians: "#8E3A80",
  Entertainers: "#C5893C",
  Writers: "#3B5B8C",
  Scientists: "#3E6B4A",
  Politicians: "#5C3B7A",
};

const CATEGORY_ORDER = [
  "Civil Rights",
  "Athletes",
  "Musicians",
  "Entertainers",
  "Writers",
  "Scientists",
  "Politicians",
];

const DONATION_PRESETS = [5, 10, 25, 50, 100];

const fmtMoney = (n) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: n >= 1000 ? 0 : 2,
  });

const fmtInt = (n) => n.toLocaleString("en-US");

// ---------------- Sam Cooke anthem player ----------------
// We embed the official YouTube video (licensing already handled by YouTube's
// deal with rights-holders). We never autoplay — user presses play.
// Primary: Sam Cooke — "A Change Is Gonna Come" (SamCookeVEVO / ABKCO official).
const ANTHEM_VIDEO_ID = "wEBlaMOmKV4";
const ANTHEM_TITLE = "A Change Is Gonna Come";
const ANTHEM_ARTIST = "Sam Cooke · 1964";
const ANTHEM_YOUTUBE_URL = `https://www.youtube.com/watch?v=${ANTHEM_VIDEO_ID}`;

function AnthemPlayer() {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [armed, setArmed] = useState(false); // true = waiting for first user gesture

  // Auto-open the panel 1.2s after mount (unless previously dismissed)
  // and arm a global first-gesture listener that kicks off playback.
  useEffect(() => {
    if (sessionStorage.getItem("anthem-dismissed") === "1") {
      setDismissed(true);
      return;
    }
    const openTimer = setTimeout(() => {
      setOpen(true);
      setArmed(true);
    }, 1200);
    return () => clearTimeout(openTimer);
  }, []);

  // When armed, start playback on the first user interaction anywhere on the page.
  // This satisfies the browser autoplay policy (sound-on requires a user gesture).
  useEffect(() => {
    if (!armed) return;
    const kick = () => {
      setLoaded(true);
      setArmed(false);
    };
    window.addEventListener("pointerdown", kick, { once: true, capture: true });
    window.addEventListener("touchstart", kick, { once: true, capture: true });
    window.addEventListener("keydown", kick, { once: true, capture: true });
    window.addEventListener("scroll", kick, { once: true, capture: true });
    return () => {
      window.removeEventListener("pointerdown", kick, true);
      window.removeEventListener("touchstart", kick, true);
      window.removeEventListener("keydown", kick, true);
      window.removeEventListener("scroll", kick, true);
    };
  }, [armed]);

  if (dismissed) return null;

  const close = () => {
    setOpen(false);
    setLoaded(false);
    setArmed(false);
  };

  const dismiss = (e) => {
    e.stopPropagation();
    setDismissed(true);
    sessionStorage.setItem("anthem-dismissed", "1");
  };

  return (
    <div
      className={`anthem-dock ${open ? "anthem-open" : ""}`}
      data-testid="anthem-dock"
    >
      {open ? (
        <div className="anthem-panel" data-testid="anthem-panel">
          <div className="anthem-head">
            <div className="anthem-titles">
              <span className="anthem-label">
                <Music size={12} /> THE ANTHEM
              </span>
              <h4>{ANTHEM_TITLE}</h4>
              <p>{ANTHEM_ARTIST}</p>
            </div>
            <button
              className="anthem-close"
              onClick={close}
              aria-label="Close"
              data-testid="anthem-close-btn"
            >
              <X size={18} />
            </button>
          </div>
          <div className="anthem-frame">
            {loaded ? (
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${ANTHEM_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
                title="Sam Cooke — A Change Is Gonna Come"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="anthem-iframe"
              />
            ) : (
              <button
                className="anthem-play"
                onClick={() => setLoaded(true)}
                data-testid="anthem-play-btn"
              >
                <Play size={28} />
                <span>{armed ? "Tap anywhere to play" : "Play here"}</span>
                <small>
                  {armed
                    ? "Sam Cooke · 1964 · any tap starts it"
                    : "via YouTube · no account needed"}
                </small>
              </button>
            )}
          </div>
          <div className="anthem-fallback">
            <a
              href={ANTHEM_YOUTUBE_URL}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="anthem-youtube-link"
            >
              <ExternalLink size={12} />
              Open in YouTube
            </a>
          </div>
          <p className="anthem-foot">
            <em>
              &ldquo;It's been a long, a long time coming — but I know a change gonna come.&rdquo;
            </em>
          </p>
        </div>
      ) : (
        <button
          className="anthem-pill"
          onClick={() => setOpen(true)}
          data-testid="anthem-pill-btn"
        >
          <span className="anthem-pill-icon">
            <Music size={16} />
          </span>
          <span className="anthem-pill-text">
            <b>Listen: {ANTHEM_TITLE}</b>
            <small>{ANTHEM_ARTIST}</small>
          </span>
          <span className="anthem-pill-dismiss" onClick={dismiss} role="button" aria-label="Dismiss">
            <X size={12} />
          </span>
        </button>
      )}
    </div>
  );
}

// ---------------- Components ----------------

function GoalGauge({ stats }) {
  const pct = Math.min(100, stats?.progress_pct || 0);
  return (
    <div className="gauge" data-testid="goal-gauge">
      <div className="gauge-head">
        <div>
          <div className="gauge-eyebrow">
            <Target size={14} /> $1,000,000 GOAL
          </div>
          <div className="gauge-raised" data-testid="gauge-raised">
            {fmtMoney(stats?.total_raised_usd || 0)}
          </div>
          <div className="gauge-sub">raised of {fmtMoney(stats?.goal_usd || 1e6)}</div>
        </div>
        <div className="gauge-meta">
          <div className="gauge-meta-row">
            <span>{fmtInt(stats?.copies_sold || 0)}</span>
            <small>books sold</small>
          </div>
          <div className="gauge-meta-row">
            <span>{fmtInt(stats?.donations_count || 0)}</span>
            <small>donations</small>
          </div>
          <div className="gauge-meta-row">
            <span>{pct.toFixed(2)}%</span>
            <small>of goal</small>
          </div>
        </div>
      </div>
      <div className="gauge-bar">
        <div
          className="gauge-fill"
          style={{ width: `${Math.max(pct, 0.3)}%` }}
          data-testid="gauge-fill"
        />
        <div className="gauge-ticks">
          {[25, 50, 75].map((t) => (
            <div key={t} className="gauge-tick" style={{ left: `${t}%` }}>
              <span>{t}%</span>
            </div>
          ))}
        </div>
      </div>
      {stats?.recent_supporters?.length > 0 && (
        <div className="gauge-supporters" data-testid="recent-supporters">
          <span className="gauge-supporters-label">Recent supporters:</span>
          {stats.recent_supporters.slice(0, 5).map((s, i) => (
            <span key={i} className="supporter-chip">
              {s.name}
              <small>{s.kind === "donation" ? "donated" : "bought"} {fmtMoney(s.amount_usd)}</small>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function PurchaseWidget({ paypalConfigured, onSuccess }) {
  const [tab, setTab] = useState("book"); // book | hardcopy | donation
  const [copies, setCopies] = useState(1);
  const [hardcopies, setHardcopies] = useState(1);
  const [donateAmt, setDonateAmt] = useState(25);
  const [customAmt, setCustomAmt] = useState("");
  const [includeBook, setIncludeBook] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  // Shipping (hardcopy only)
  const [ship, setShip] = useState({
    name: "",
    street: "",
    street2: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const amount = useMemo(() => {
    if (tab === "book") return Math.max(1, copies) * 1;
    if (tab === "hardcopy") return Math.max(1, hardcopies) * 24.99;
    const amt = customAmt ? parseFloat(customAmt) : donateAmt;
    return amt && amt >= 1 ? Math.round(amt * 100) / 100 : 0;
  }, [tab, copies, hardcopies, donateAmt, customAmt]);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validShip =
    tab !== "hardcopy" ||
    (ship.name.trim().length >= 2 &&
      ship.street.trim().length >= 3 &&
      ship.city.trim().length >= 2 &&
      ship.state.trim().length >= 2 &&
      ship.zip.trim().length >= 3);

  const canCheckout = amount >= 1 && validEmail && validShip && paypalConfigured;

  const buildPayload = () => {
    const base = {
      kind: tab,
      copies: tab === "book" ? copies : tab === "hardcopy" ? hardcopies : 1,
      custom_amount: tab === "donation" ? amount : null,
      include_book: tab === "donation" ? includeBook : false,
      email: email.trim(),
      name: name.trim() || null,
    };
    if (tab === "hardcopy") {
      base.shipping = {
        name: ship.name.trim(),
        street: ship.street.trim(),
        street2: ship.street2.trim() || null,
        city: ship.city.trim(),
        state: ship.state.trim(),
        zip: ship.zip.trim(),
        country: "US",
        phone: ship.phone.trim() || null,
      };
    }
    return base;
  };

  const internalRef = useRef(null);

  const createOrder = async () => {
    setError("");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/orders`, buildPayload());
      internalRef.current = r.data.order_id;
      return r.data.paypal_order_id;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start checkout.");
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const onApprove = async () => {
    if (!internalRef.current) {
      setError("Lost order reference. Please retry.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const r = await axios.post(`${API}/orders/${internalRef.current}/capture`);
      onSuccess({ ...r.data, kind: tab });
    } catch (e) {
      setError(e?.response?.data?.detail || "Capture failed.");
    } finally {
      setBusy(false);
    }
  };

  const updShip = (k, v) => setShip((p) => ({ ...p, [k]: v }));

  return (
    <div className="buy-widget" data-testid="buy-widget">
      <div className="buy-tabs">
        <button
          className={`buy-tab ${tab === "book" ? "active" : ""}`}
          onClick={() => setTab("book")}
          data-testid="tab-book-btn"
        >
          <BookOpen size={16} /> Digital · $1
        </button>
        <button
          className={`buy-tab ${tab === "hardcopy" ? "active" : ""}`}
          onClick={() => setTab("hardcopy")}
          data-testid="tab-hardcopy-btn"
        >
          <Package size={16} /> Hard Copy · $24.99
        </button>
        <button
          className={`buy-tab ${tab === "donation" ? "active" : ""}`}
          onClick={() => setTab("donation")}
          data-testid="tab-donation-btn"
        >
          <Heart size={16} /> Donate
        </button>
      </div>

      <div className="buy-body">
        {tab === "book" ? (
          <>
            <label className="buy-label">Number of copies ($1 each)</label>
            <div className="copies-row">
              <button
                className="qty-btn"
                onClick={() => setCopies(Math.max(1, copies - 1))}
                data-testid="copies-minus-btn"
              >
                −
              </button>
              <input
                type="number"
                min="1"
                max="500"
                value={copies}
                onChange={(e) =>
                  setCopies(Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))
                }
                className="qty-input"
                data-testid="copies-input"
              />
              <button
                className="qty-btn"
                onClick={() => setCopies(Math.min(500, copies + 1))}
                data-testid="copies-plus-btn"
              >
                +
              </button>
              <div className="qty-presets">
                {[1, 5, 10, 25].map((n) => (
                  <button
                    key={n}
                    className={`qty-preset ${copies === n ? "active" : ""}`}
                    onClick={() => setCopies(n)}
                    data-testid={`copies-preset-${n}-btn`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <p className="buy-hint">
              <Gift size={14} />
              Get {copies} unique download link{copies > 1 ? "s" : ""} — forward to friends & family as a
              gift.
            </p>
          </>
        ) : tab === "hardcopy" ? (
          <>
            <div className="hardcopy-banner" data-testid="hardcopy-banner">
              <Package size={20} />
              <div>
                <b>Printed &amp; bound, shipped to your door.</b>
                <span>
                  $24.99 each includes the digital PDF too. US shipping only for now. Ships
                  in 2–3 weeks as orders are printed in batches.
                </span>
              </div>
            </div>
            <label className="buy-label">Hard copies ($24.99 each)</label>
            <div className="copies-row">
              <button
                className="qty-btn"
                onClick={() => setHardcopies(Math.max(1, hardcopies - 1))}
                data-testid="hardcopies-minus-btn"
              >
                −
              </button>
              <input
                type="number"
                min="1"
                max="50"
                value={hardcopies}
                onChange={(e) =>
                  setHardcopies(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))
                }
                className="qty-input"
                data-testid="hardcopies-input"
              />
              <button
                className="qty-btn"
                onClick={() => setHardcopies(Math.min(50, hardcopies + 1))}
                data-testid="hardcopies-plus-btn"
              >
                +
              </button>
              <div className="qty-presets">
                {[1, 2, 5, 10].map((n) => (
                  <button
                    key={n}
                    className={`qty-preset ${hardcopies === n ? "active" : ""}`}
                    onClick={() => setHardcopies(n)}
                    data-testid={`hardcopies-preset-${n}-btn`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="ship-form" data-testid="ship-form">
              <div className="ship-head">
                <Truck size={16} /> Shipping address
              </div>
              <div className="ship-grid">
                <div className="field col-2">
                  <label>Full name</label>
                  <input
                    value={ship.name}
                    onChange={(e) => updShip("name", e.target.value)}
                    placeholder="Your full name"
                    data-testid="ship-name-input"
                  />
                </div>
                <div className="field col-2">
                  <label>Street address</label>
                  <input
                    value={ship.street}
                    onChange={(e) => updShip("street", e.target.value)}
                    placeholder="123 Main St"
                    data-testid="ship-street-input"
                  />
                </div>
                <div className="field col-2">
                  <label>Apartment / Unit (optional)</label>
                  <input
                    value={ship.street2}
                    onChange={(e) => updShip("street2", e.target.value)}
                    placeholder="Apartment, suite, etc. (optional)"
                    data-testid="ship-street2-input"
                  />
                </div>
                <div className="field">
                  <label>City</label>
                  <input
                    value={ship.city}
                    onChange={(e) => updShip("city", e.target.value)}
                    placeholder="City"
                    data-testid="ship-city-input"
                  />
                </div>
                <div className="field">
                  <label>State</label>
                  <input
                    value={ship.state}
                    onChange={(e) => updShip("state", e.target.value.toUpperCase().slice(0, 2))}
                    placeholder="State (2-letter)"
                    maxLength={2}
                    data-testid="ship-state-input"
                  />
                </div>
                <div className="field">
                  <label>ZIP code</label>
                  <input
                    value={ship.zip}
                    onChange={(e) => updShip("zip", e.target.value)}
                    placeholder="ZIP code"
                    data-testid="ship-zip-input"
                  />
                </div>
                <div className="field">
                  <label>Phone (optional, for delivery issues)</label>
                  <input
                    value={ship.phone}
                    onChange={(e) => updShip("phone", e.target.value)}
                    placeholder="(555) 123-4567"
                    data-testid="ship-phone-input"
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <label className="buy-label">Choose an amount</label>
            <div className="donate-row">
              {DONATION_PRESETS.map((a) => (
                <button
                  key={a}
                  className={`donate-preset ${donateAmt === a && !customAmt ? "active" : ""}`}
                  onClick={() => {
                    setDonateAmt(a);
                    setCustomAmt("");
                  }}
                  data-testid={`donate-preset-${a}-btn`}
                >
                  ${a}
                </button>
              ))}
              <input
                type="number"
                min="1"
                max="100000"
                step="1"
                placeholder="Custom $"
                value={customAmt}
                onChange={(e) => setCustomAmt(e.target.value)}
                className="donate-custom"
                data-testid="donate-custom-input"
              />
            </div>
            <label className="include-row" data-testid="include-book-label">
              <input
                type="checkbox"
                checked={includeBook}
                onChange={(e) => setIncludeBook(e.target.checked)}
                data-testid="include-book-checkbox"
              />
              <span>
                <b>Send me the book as well</b> — a copy will be unlocked along with your
                donation.
              </span>
            </label>
          </>
        )}

        <div className="buy-fields">
          <div className="field">
            <label>Email (for your download link)</label>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="email-input"
            />
          </div>
          <div className="field">
            <label>Name (optional, shown on the supporter wall)</label>
            <input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="name-input"
            />
          </div>
        </div>

        <div className="buy-total" data-testid="buy-total">
          <span>Total</span>
          <b>{fmtMoney(amount)}</b>
        </div>

        {error && (
          <div className="buy-error" data-testid="buy-error">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {!paypalConfigured && (
          <div className="buy-warn" data-testid="paypal-not-configured">
            <AlertCircle size={14} />
            PayPal is not yet configured. Add PAYPAL_CLIENT_ID &amp; PAYPAL_CLIENT_SECRET in
            <code> backend/.env</code> and restart.
          </div>
        )}

        {paypalConfigured && canCheckout && (
          <div className="paypal-host" data-testid="paypal-host">
            {busy && <div className="paypal-overlay"><Loader2 size={20} className="spin" /> processing…</div>}

            <div className="pay-options-hint" data-testid="pay-options-hint">
              <CreditCard size={16} />
              <span>
                Pay with <b>PayPal</b>, or use any <b>debit or credit card</b> —
                no PayPal account needed. Tap the gold button below, then choose
                <b> &ldquo;Pay with Debit or Credit Card&rdquo;</b> on the next screen.
              </span>
            </div>

            <PayPalButtons
              style={{
                layout: "vertical",
                color: "gold",
                shape: "pill",
                label: tab === "donation" ? "donate" : "paypal",
              }}
              fundingSource={undefined}
              forceReRender={[amount, tab, copies, hardcopies, email, includeBook, ship.zip]}
              createOrder={createOrder}
              onApprove={onApprove}
              onCancel={() => setError("")}
              onError={(err) => {
                console.error(err);
                setError("PayPal error. Please try again.");
              }}
            />
          </div>
        )}

        {paypalConfigured && !canCheckout && (
          <div className="buy-disabled-hint" data-testid="buy-disabled-hint">
            Enter a valid email and amount ≥ $1 to unlock checkout.
          </div>
        )}

        {!paypalConfigured && amount >= 1 && !validEmail && email.length > 0 && (
          <div className="buy-disabled-hint" data-testid="buy-email-hint">
            That doesn't look like a valid email — please double-check.
          </div>
        )}

        <p className="buy-trust">
          <span className="dot-green" /> Secure checkout via PayPal · credit & debit cards
          accepted · no PayPal account required
        </p>
      </div>
    </div>
  );
}

// ---------------- Recover Downloads ----------------
function RecoverDownloads() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!valid) {
      setError("Please enter a valid email.");
      return;
    }
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const r = await axios.post(`${API}/download/resend`, { email: email.trim() });
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="recover-card" data-testid="recover-card">
      <div className="recover-head">
        <div className="recover-icon">
          <Download size={22} />
        </div>
        <div>
          <h3>Lost your download link?</h3>
          <p>
            Enter the email you used at checkout — we'll show every download link on this
            device right now, no sign-in needed.
          </p>
        </div>
      </div>
      <form className="recover-form" onSubmit={submit} data-testid="recover-form">
        <input
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="recover-email-input"
        />
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!valid || busy}
          data-testid="recover-submit-btn"
        >
          {busy ? <Loader2 size={16} className="spin" /> : <Download size={16} />}
          {busy ? "Searching…" : "Get my links"}
        </button>
      </form>

      {error && (
        <div className="buy-error" data-testid="recover-error">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {result && (
        <div className={`recover-result ${result.found ? "found" : "empty"}`} data-testid="recover-result">
          {result.found ? (
            <>
              <div className="recover-result-head">
                <CheckCircle2 size={18} /> <b>{result.message}</b>
              </div>
              {result.orders.map((o, oi) => (
                <div key={o.order_id} className="recover-order" data-testid={`recover-order-${oi}`}>
                  <div className="recover-order-head">
                    <span>
                      {o.kind === "hardcopy" ? "Hard Copy" : o.kind === "donation" ? "Donation" : "Digital"}
                      {" · "}
                      {fmtMoney(o.amount_usd)}
                      {" · "}
                      {o.copies} cop{o.copies === 1 ? "y" : "ies"}
                    </span>
                    {o.captured_at && (
                      <small>{new Date(o.captured_at).toLocaleDateString()}</small>
                    )}
                  </div>
                  <div className="token-list">
                    {o.tokens.map((t, i) => {
                      const url = `${API}/download/book/${t.token}`;
                      const remaining = Math.max(0, t.max_uses - t.uses);
                      return (
                        <div key={t.token} className="token-row" data-testid={`recover-token-${oi}-${i}`}>
                          <span className="token-idx">Copy {i + 1}</span>
                          <code>{url}</code>
                          <a
                            href={url}
                            className="btn-ico"
                            title={`${remaining}/${t.max_uses} downloads remaining`}
                            data-testid={`recover-download-${oi}-${i}-link`}
                          >
                            <Download size={14} />
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="recover-empty">
              <AlertCircle size={18} /> {result.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SuccessModal({ success, onClose }) {
  const [copiedIdx, setCopiedIdx] = useState(-1);
  if (!success) return null;
  const { tokens, amount_usd, copies, kind } = success;
  const isHardcopy = kind === "hardcopy";

  const copy = async (url, i) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedIdx(i);
      setTimeout(() => setCopiedIdx(-1), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <div className="modal-shell" role="dialog" data-testid="success-modal">
      <div className="modal-card">
        <div className="modal-tri">
          <CheckCircle2 size={38} />
        </div>
        <h2>Thank you.</h2>
        <p className="modal-sub">
          Your contribution of <b>{fmtMoney(amount_usd)}</b> has been received.
          {isHardcopy && (
            <>
              {" "}Your <b>{copies}</b> hard cop{copies === 1 ? "y ships" : "ies ship"} in
              2–3 weeks. You'll also get the digital PDF instantly below.
            </>
          )}
          {!isHardcopy && tokens.length > 0 && (
            <>
              {" "}
              You have <b>{tokens.length}</b> download link{tokens.length > 1 ? "s" : ""} — one per
              copy. Share with friends and family as a gift.
            </>
          )}
        </p>
        {tokens.length === 0 && (
          <p className="modal-sub">Your donation goes straight to the $1M goal. ✨</p>
        )}
        <div className="token-list">
          {tokens.map((tok, i) => {
            const url = `${API}/download/book/${tok}`;
            return (
              <div key={tok} className="token-row" data-testid={`token-row-${i}`}>
                <span className="token-idx">Copy {i + 1}</span>
                <code>{url}</code>
                <a
                  href={url}
                  className="btn-ico"
                  title="Download PDF"
                  data-testid={`token-download-${i}-link`}
                >
                  <Download size={14} />
                </a>
                <button
                  className="btn-ico"
                  onClick={() => copy(url, i)}
                  title="Copy link"
                  data-testid={`token-copy-${i}-btn`}
                >
                  {copiedIdx === i ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                </button>
              </div>
            );
          })}
        </div>
        <button className="btn btn-primary btn-lg" onClick={onClose} data-testid="success-close-btn">
          Close
        </button>
        <p className="modal-footnote">
          {isHardcopy
            ? "You'll receive a shipping confirmation once your hard copies go out. Save your digital links above."
            : "Each link is good for up to 10 downloads. Save your copies somewhere safe."}
        </p>
      </div>
    </div>
  );
}

function FigureCard({ figure }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div
      className="figure-card"
      data-testid={`figure-card-${figure.id}`}
      style={{ "--accent": CATEGORY_COLORS[figure.category] || "#9A3B2C" }}
    >
      <div className="figure-portrait-wrap">
        {figure.has_portrait ? (
          <img
            src={`${API}/portrait/${figure.id}`}
            alt={figure.name}
            onLoad={() => setLoaded(true)}
            className={loaded ? "loaded" : ""}
          />
        ) : (
          <div className="figure-placeholder">pending</div>
        )}
        <div className="figure-badge">{figure.category}</div>
      </div>
      <div className="figure-info">
        <h3>{figure.name}</h3>
        <p className="figure-years">{figure.years}</p>
      </div>
    </div>
  );
}

// ---------------- Share Bar ----------------
// Canonical public URL for shares (survives deep-links & preview envs).
const PUBLIC_SITE_URL = "https://africanamericanxperience.com";
const SHARE_HEADLINE =
  "Stones the Builder Refused — 50 African Americans who built this country. $1.";
const SHARE_BODY =
  "A tribute book by NJ Griffin. When they take it out of schools, we put it in the home. Get the PDF for $1 or a hard copy for $24.99. Every copy pushes the meter toward $1M / 1M households.";
const SHARE_HASHTAGS = "BlackHistory,Juneteenth,NJGriffin,StonesTheBuilderRefused";

function ShareBar({ variant = "row", compact = false }) {
  const [copied, setCopied] = useState(false);
  const url = PUBLIC_SITE_URL;
  const text = SHARE_HEADLINE;

  const enc = encodeURIComponent;
  const links = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(text)}`,
    twitter: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(text)}&hashtags=${enc(SHARE_HASHTAGS)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
    whatsapp: `https://api.whatsapp.com/send?text=${enc(text + " " + url)}`,
    email: `mailto:?subject=${enc(text)}&body=${enc(SHARE_BODY + "\n\n" + url)}`,
    sms: `sms:?&body=${enc(text + " " + url)}`,
  };

  const share = async (e, platform) => {
    // Use native share sheet on mobile if available
    if (platform === "native" && navigator.share) {
      e?.preventDefault?.();
      try {
        await navigator.share({ title: text, text: SHARE_BODY, url });
      } catch {
        // cancelled
      }
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore
    }
  };

  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <div
      className={`sharebar sharebar-${variant} ${compact ? "sharebar-compact" : ""}`}
      data-testid="share-bar"
    >
      {!compact && (
        <span className="sharebar-label">
          <Share2 size={14} /> Spread the word
        </span>
      )}
      <div className="sharebar-btns">
        <a
          className="share-btn share-fb"
          href={links.facebook}
          target="_blank"
          rel="noreferrer noopener"
          aria-label="Share on Facebook"
          data-testid="share-facebook-btn"
        >
          <Facebook size={16} /> <span>Facebook</span>
        </a>
        <a
          className="share-btn share-x"
          href={links.twitter}
          target="_blank"
          rel="noreferrer noopener"
          aria-label="Share on X (Twitter)"
          data-testid="share-x-btn"
        >
          <Twitter size={16} /> <span>X</span>
        </a>
        <a
          className="share-btn share-wa"
          href={links.whatsapp}
          target="_blank"
          rel="noreferrer noopener"
          aria-label="Share on WhatsApp"
          data-testid="share-whatsapp-btn"
        >
          <MessageCircle size={16} /> <span>WhatsApp</span>
        </a>
        <a
          className="share-btn share-li"
          href={links.linkedin}
          target="_blank"
          rel="noreferrer noopener"
          aria-label="Share on LinkedIn"
          data-testid="share-linkedin-btn"
        >
          <Linkedin size={16} /> <span>LinkedIn</span>
        </a>
        <a
          className="share-btn share-em"
          href={links.email}
          aria-label="Share via Email"
          data-testid="share-email-btn"
        >
          <Mail size={16} /> <span>Email</span>
        </a>
        <button
          className="share-btn share-ig"
          onClick={copy}
          aria-label="Copy link for Instagram"
          title="Copy link — paste into your Instagram bio or story"
          data-testid="share-instagram-btn"
        >
          <Instagram size={16} /> <span>Instagram</span>
        </button>
        <button
          className={`share-btn share-copy ${copied ? "copied" : ""}`}
          onClick={copy}
          aria-label="Copy link"
          data-testid="share-copy-btn"
        >
          {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
          <span>{copied ? "Copied" : "Copy link"}</span>
        </button>
        {canNativeShare && (
          <button
            className="share-btn share-native"
            onClick={(e) => share(e, "native")}
            aria-label="Open share sheet"
            data-testid="share-native-btn"
          >
            <Share2 size={16} /> <span>More</span>
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------- Marketing Commercial ----------------
// On-site cinematic promo with AI-generated (Sora 2) video.
// Video generation is admin-triggered; frontend polls status and swaps in the
// real MP4 the moment it's ready. Falls back to a stylized poster section
// with the Sam Cooke anthem so the section never looks empty.

function CommercialSection() {
  const [status, setStatus] = useState(null);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/commercial/status`);
        if (!cancelled) setStatus(r.data);
      } catch {
        // ignore
      }
    };
    poll();
    const id = setInterval(poll, status?.ready ? 60000 : 10000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.ready]);

  const videoUrl = `${API}/commercial/video?v=${status?.created_at || "latest"}`;

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section className="section commercial-section" id="commercial" data-testid="commercial-section">
      <div className="section-head">
        <span className="section-tag">00</span>
        <h2>The commercial</h2>
        <p>
          A thirty-second spot for the movement. Watch it, share it, post it.
          Every play, every share puts another name back into a home.
        </p>
      </div>

      <div className="commercial-stage" data-testid="commercial-stage">
        {status?.ready ? (
          <div className="commercial-video-wrap">
            <video
              ref={videoRef}
              className="commercial-video"
              src={videoUrl}
              autoPlay
              loop
              muted={muted}
              playsInline
              controls={false}
              data-testid="commercial-video"
            />
            <button
              className={`commercial-mute ${muted ? "pulse" : ""}`}
              onClick={toggleMute}
              aria-label={muted ? "Unmute" : "Mute"}
              data-testid="commercial-mute-btn"
            >
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              <span>{muted ? "Tap for narration" : "Narration on"}</span>
            </button>
            <div className="commercial-badge">
              <Film size={12} /> Civil Rights · 1963 · 1964
            </div>
            <div className="commercial-overlay-text">
              <div className="commercial-tagline">
                Stones the Builder Refused
              </div>
              <div className="commercial-sub">
                50 African Americans who built this country · $1
              </div>
            </div>
          </div>
        ) : (
          <div
            className="commercial-poster"
            style={{
              backgroundImage: `url(${API}/preview/collage/1)`,
            }}
            data-testid="commercial-poster"
          >
            <div className="commercial-poster-scrim" />
            <div className="commercial-poster-content">
              <span className="commercial-badge">
                <Film size={12} />{" "}
                {status?.status === "generating"
                  ? "Loading…"
                  : status?.status === "error"
                  ? "Commercial loading…"
                  : "Civil Rights · 1963 · 1964"}
              </span>
              <h3 className="commercial-poster-title">
                When they take it out of schools,
                <br />
                <span className="accent-gold">we put it in the home.</span>
              </h3>
              <p className="commercial-poster-body">
                A 30-second spot · soundtrack: Sam Cooke, <em>"A Change Is Gonna Come."</em>{" "}
                {status?.status === "generating" &&
                  "AI film is rendering — it will auto-play here in a few minutes."}
                {!status?.status &&
                  "Watch the cinematic spot — Volume I of V is available now."}
              </p>
              <div className="commercial-poster-ctas">
                <a
                  href="#buy"
                  className="btn btn-gold btn-lg"
                  data-testid="commercial-poster-cta"
                >
                  <ShoppingCart size={18} /> Get the book · $1
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="commercial-actions" data-testid="commercial-actions">
        <ShareBar variant="row" compact />
      </div>
    </section>
  );
}

// ---------------- App ----------------

function App() {
  const [figures, setFigures] = useState([]);
  const [stats, setStats] = useState(null);
  const [paypalCfg, setPaypalCfg] = useState({ configured: false, client_id: "", mode: "sandbox" });
  const [success, setSuccess] = useState(null);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    const load = async () => {
      try {
        const [f, g, p] = await Promise.all([
          axios.get(`${API}/figures`),
          axios.get(`${API}/goal`),
          axios.get(`${API}/paypal/config`),
        ]);
        setFigures(f.data);
        setStats(g.data);
        setPaypalCfg(p.data);
      } catch (e) {
        console.error(e);
      }
    };
    load();
    const id = setInterval(async () => {
      try {
        const g = await axios.get(`${API}/goal`);
        setStats(g.data);
      } catch {
        // ignore
      }
    }, 15000);
    return () => clearInterval(id);
  }, []);

  const handleSuccess = async (resp) => {
    setSuccess({
      tokens: resp.download_tokens || [],
      amount_usd: resp.amount_usd,
      copies: resp.copies,
    });
    // refresh stats
    try {
      const g = await axios.get(`${API}/goal`);
      setStats(g.data);
    } catch {
      // ignore
    }
  };

  const categories = ["All", ...CATEGORY_ORDER];
  const shown = filter === "All" ? figures : figures.filter((f) => f.category === filter);

  // --- Launch-day social proof (toasts + ghost meter bump) ---
  const { bonus, toast: spToast } = useSocialProof();
  const displayStats = stats
    ? (() => {
        const baseRaised = stats.total_raised_usd || 0;
        const goal = stats.goal_usd || 1_000_000;
        const mergedRaised = baseRaised + bonus.raised;
        return {
          ...stats,
          total_raised_usd: mergedRaised,
          copies_sold: (stats.copies_sold || 0) + bonus.copies,
          progress_pct: Math.min(100, (mergedRaised / goal) * 100),
          recent_supporters: [
            ...bonus.supporters,
            ...(stats.recent_supporters || []),
          ].slice(0, 5),
        };
      })()
    : stats;

  // CRITICAL: never mount the PayPal SDK with a fallback "sb" sandbox client ID.
  // If the live client ID hasn't loaded yet, render the page without PayPal —
  // PurchaseWidget will simply skip rendering the PayPal buttons until config arrives.
  const hasLiveClientId = !!(paypalCfg.configured && paypalCfg.client_id);

  const appContent = (
      <div className="app" data-testid="app-root">
        <header className="hero">
          <div className="hero-inner">
            <div className="hero-eyebrow">
              <span className="dot" /> Stones the Builder Refused · Volume I
            </div>
            <h1 className="hero-title">
              Stones <span className="accent">the Builder</span>
              <br />
              Refused
            </h1>
            <p className="hero-sub">
              A $1 downloadable tribute book — or a $24.99 printed hard copy shipped to
              your door — honoring fifty lives that built and shaped America. 100% of
              proceeds fuel the mission: put African American history in the hands of a
              million households so our children know the names that built this country.
            </p>
            <p className="psalm-line">
              <em>&ldquo;The stone the builder refused has become the chief cornerstone.&rdquo;</em>
              <span>— Psalm 118:22</span>
            </p>
            <p className="signature">
              Curated &amp; designed by <b>NJ Griffin</b>
            </p>

            <a href="#buy" className="btn btn-primary btn-lg" data-testid="hero-cta-btn">
              <ShoppingCart size={18} /> Get the Book · $1 digital / $24.99 print
            </a>

            <div className="hero-secondary">
              <Link to="/story" className="hero-story-link" data-testid="hero-story-link">
                Read the founder's letter →
              </Link>
            </div>

            <ShareBar variant="hero" compact />
          </div>
        </header>

        {/* MARKETING COMMERCIAL */}
        <CommercialSection />

        {/* CALL TO ACTION — erasure */}
        <section className="section erasure" data-testid="erasure-section">
          <div className="erasure-card">
            <div className="erasure-eyebrow">
              <AlertCircle size={14} /> A CALL TO ACTION
            </div>
            <h2>
              When they take it <span className="strike">out of schools</span>,
              <br />
              we put it <span className="accent-gold">in the home</span>.
            </h2>
            <div className="erasure-body">
              <p>
                Across the United States, state governments have stripped Black
                history from public school curriculums. The AP African American
                Studies course has been banned or rewritten in multiple states.
                The contributions of the men and women in this book — names every
                child in America should know — are being erased from the classroom
                under the banner of "anti-divisive" laws.
              </p>
              <p className="erasure-pull">
                If they won't teach it,{" "}
                <b>
                  <u>we will</u>
                </b>
                .
              </p>
              <p>
                This book is a start. One dollar. One household. One million
                children who will know these names. Our history won't pass on by
                itself — it's on us to carry it.
              </p>
            </div>
            <div className="erasure-cta">
              <a href="#buy" className="btn btn-gold btn-lg" data-testid="erasure-cta-btn">
                <ShoppingCart size={18} /> Put It in Your Home · $1
              </a>
              <span className="erasure-sub">
                · or pass it forward — every copy bought puts it into another
                household ·
              </span>
            </div>
          </div>
        </section>

        {/* MISSION */}
        <section className="section mission" data-testid="mission-section">
          <div className="section-head">
            <span className="section-tag">01</span>
            <h2>Why this book exists</h2>
            <p>
              African Americans have played an extraordinary part in the building and
              making of this country — contributions that have too often been hidden from
              history. This book is a small dedication to some of the men and women who
              have been a part of that enduring legacy.
            </p>
          </div>
          <div className="mission-grid">
            <div className="mission-card" data-testid="mission-card-1">
              <GraduationCap size={24} />
              <h3>Teach the children</h3>
              <p>
                Fifty one-page profiles, large portraits, lifespans, and bios — the
                perfect first encyclopedia for a young reader. From Harriet Tubman and
                Frederick Douglass to Katherine Johnson and Barack Obama.
              </p>
            </div>
            <div className="mission-card" data-testid="mission-card-2">
              <BookOpen size={24} />
              <h3>Printable &amp; gifted</h3>
              <p>
                Legal-size (8.5 × 14″) PDF — print at home, at a library, at a copy
                shop. Buy multiple copies and send unique download links to teachers,
                grandparents, and nieces and nephews. A $1 gift that lasts a lifetime.
              </p>
            </div>
            <div className="mission-card" data-testid="mission-card-3">
              <Target size={24} />
              <h3>One million households</h3>
              <p>
                Every $1 is one more family reached. The goal is $1,000,000 — a million
                households carrying this record of who built this country. Volume I is
                the first of five.
              </p>
            </div>
          </div>
        </section>

        {/* GOAL GAUGE */}
        <section className="section goal-section" id="goal" data-testid="goal-section">
          <div className="section-head">
            <span className="section-tag">02</span>
            <h2>Our march to $1,000,000</h2>
            <p>Every book sold and every dollar donated pushes the meter forward. Real-time.</p>
          </div>
          <GoalGauge stats={displayStats} />
        </section>

        {/* PURCHASE */}
        <section className="section buy-section" id="buy" data-testid="buy-section">
          <div className="section-head">
            <span className="section-tag">03</span>
            <h2>Get the book · digital $1 · hard copy $24.99 · or donate</h2>
            <p>
              Secure checkout via PayPal — credit & debit cards accepted, no PayPal
              account required. Digital links delivered on the success screen; hard
              copies ship to your door.
            </p>
          </div>
          <PurchaseWidget
            paypalConfigured={paypalCfg.configured}
            onSuccess={handleSuccess}
          />
        </section>

        {/* SHARE SECTION — invite others into the mission */}
        <section className="section share-section" id="share" data-testid="share-section">
          <div className="section-head">
            <span className="section-tag">★</span>
            <h2>Share the mission</h2>
            <p>
              You don&rsquo;t have to buy to help. One share, one post, one text puts this
              in front of another household.
            </p>
          </div>
          <ShareBar variant="wide" />
        </section>

        {/* RECOVER DOWNLOADS */}
        <section className="section recover-section" id="recover" data-testid="recover-section">
          <div className="section-head">
            <span className="section-tag">04</span>
            <h2>Already bought it? Get your link back.</h2>
            <p>
              If your download link got lost, your phone cleared it, or something went
              sideways at checkout — put the email you used below and your links will
              reappear right here.
            </p>
          </div>
          <RecoverDownloads />
        </section>

        {/* FIGURES PREVIEW */}
        <section className="section gallery" data-testid="gallery-section">
          <div className="section-head">
            <span className="section-tag">05</span>
            <h2>Who you'll meet inside</h2>
            <p>
              Fifty figures across civil rights, athletics, music, letters, science and
              statecraft. Each with a portrait, lifespan, and short biography. All for $1.
            </p>
          </div>
          <div className="filter-row" data-testid="filter-row">
            {categories.map((c) => (
              <button
                key={c}
                className={`chip ${filter === c ? "chip-active" : ""}`}
                onClick={() => setFilter(c)}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}-btn`}
                style={c !== "All" ? { "--accent": CATEGORY_COLORS[c] } : {}}
              >
                {c}
                <span className="chip-count">
                  {c === "All"
                    ? figures.length
                    : figures.filter((f) => f.category === c).length}
                </span>
              </button>
            ))}
          </div>
          <div className="figure-grid">
            {shown.map((f) => (
              <FigureCard key={f.id} figure={f} />
            ))}
          </div>
          <a href="#buy" className="btn btn-primary btn-lg gallery-cta" data-testid="gallery-cta-btn">
            <ShoppingCart size={18} /> Get all 50 — $1
          </a>
        </section>

        {/* FAQ / DETAILS */}
        <section className="section faq" data-testid="faq-section">
          <div className="section-head">
            <span className="section-tag">06</span>
            <h2>Details</h2>
          </div>
          <div className="faq-grid">
            <div className="faq-item">
              <h4>What exactly do I get?</h4>
              <p>
                <b>$1 digital:</b> a <b>57-page PDF</b> (cover · dedication · 50 profiles
                · summary grid), Legal size, 300 DPI — print at any copy shop.
                <br />
                <b>$24.99 hard copy:</b> professionally printed &amp; bound edition shipped
                to your door — <i>plus</i> the digital PDF as a bonus.
              </p>
            </div>
            <div className="faq-item">
              <h4>Can I buy copies as gifts?</h4>
              <p>
                Yes. Digital: 1–500 copies, one unique download link per copy. Hard copy:
                1–50 books shipped to one US address (email us for bulk classroom orders
                to multiple addresses).
              </p>
            </div>
            <div className="faq-item">
              <h4>How long does shipping take?</h4>
              <p>
                Hard copies print in <b>2–3 week batches</b>. We'll send a tracking
                confirmation the day your book ships. US shipping only at this time.
              </p>
            </div>
            <div className="faq-item">
              <h4>Is my payment secure?</h4>
              <p>
                All payments are processed by PayPal on their servers — the site never
                touches your card details. Credit and debit cards are accepted without
                needing a PayPal account.
              </p>
            </div>
            <div className="faq-item">
              <h4>Where does the money go?</h4>
              <p>
                Straight toward the $1,000,000 goal — funding Volumes II–V and putting
                printable Black history into a million households.
              </p>
            </div>
            <div className="faq-item">
              <h4>Can I just donate?</h4>
              <p>
                Absolutely. Choose <b>Donate</b> above, pick any amount, and optionally
                send yourself a copy of the book with it.
              </p>
            </div>
            <div className="faq-item">
              <h4>Volume II?</h4>
              <p>
                This is <b>Volume I of a five-part series</b>. Volume II is in the works.
                Supporters get first notice.
              </p>
            </div>
          </div>
        </section>

        <footer className="footer">
          <div className="footer-rule" />
          <p>
            Stones the Builder Refused · Volume I · Portraits sourced from Wikipedia /
            Wikimedia Commons (public domain &amp; Creative Commons).
          </p>
          <p className="sig">
            <em>Done by</em> <b>NJ Griffin</b>
          </p>
        </footer>

        <SuccessModal success={success} onClose={() => setSuccess(null)} />
        <AnthemPlayer />
        <SocialProofToast toast={spToast} />
      </div>
  );

  // Only mount PayPal SDK once we have the live client ID — prevents the
  // sandbox "sb" fallback from ever loading.
  const home = hasLiveClientId ? (
    <PayPalScriptProvider
      options={{
        "client-id": paypalCfg.client_id,
        currency: "USD",
        intent: "capture",
        components: "buttons",
        "disable-funding": "credit,paylater,card",
      }}
    >
      {appContent}
    </PayPalScriptProvider>
  ) : (
    appContent
  );

  return (
    <Routes>
      <Route path="/" element={home} />
      <Route path="/story" element={<StoryPage />} />
      <Route path="/studio" element={<StudioPage />} />
      <Route path="/press" element={<PressPage />} />
      <Route path="/playbook" element={<PlaybookPage />} />
    </Routes>
  );
}

export default App;
